import React, { useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import AccountGroupOutlineIcon from 'mdi-react/AccountGroupOutlineIcon';
import AccountMultipleOutlineIcon from 'mdi-react/AccountMultipleOutlineIcon';
import AccountOutlineIcon from 'mdi-react/AccountOutlineIcon';
import LockOutlineIcon from 'mdi-react/LockOutlineIcon';
import type Room from '../models/room/room';
import * as styles from '../office-workspace.styles';
import { roomAccentFor, roomKindFor } from '../room-kind';
import EditRoomModal from '../modals/edit-room-modal.component';
import AppConstants from '../../../core/app.constants';

interface Props {
  readonly initialRoom: Room;
  readonly onDelete: (value: Room, acknowledged: boolean) => void;
  readonly isEmptyOrInUse: (room: Room) => boolean;
  readonly handleUpdateRoom: (value: Room) => void;
  readonly canEditCapacity: boolean;
  readonly floorplanEnabled: boolean;
  /** Departments this room is assigned to — shown as the card's subtitle. */
  readonly departmentNames?: string[];
  /** Largest capacity in the office, used to scale the capacity bar. */
  readonly capacityScale?: number;
}

const KIND_ICONS = {
  single: AccountOutlineIcon,
  multi: AccountMultipleOutlineIcon,
  shared: AccountGroupOutlineIcon,
};

const RoomCard = ({
  initialRoom,
  onDelete,
  isEmptyOrInUse,
  handleUpdateRoom: handleChange,
  canEditCapacity,
  floorplanEnabled,
  departmentNames,
  capacityScale,
}: Props): React.JSX.Element => {
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [room] = useState<Room>(initialRoom);

  const { t } = useTranslation();

  // Icon and colour come from the room's size (see room-kind); the subtitle stays real data.
  const accent = roomAccentFor(room.capacity);
  const KindIcon = KIND_ICONS[roomKindFor(room.capacity)];
  const subtitle = departmentNames && departmentNames.length > 0 ? departmentNames.join(', ') : t('no-department');
  const scale = capacityScale && capacityScale > 0 ? capacityScale : room.capacity;
  const barPercentage = Math.min(100, Math.max(4, (room.capacity / (scale || 1)) * 100));

  return (
    <>
      <div
        className={style(styles.roomCard)}
        role="button"
        tabIndex={0}
        onClick={(): void => setShowRoomModal(true)}
        onKeyDown={(event): void => {
          if (event.key === 'Enter' || event.key === ' ') {
            // Space would scroll the page as well as opening the modal.
            event.preventDefault();
            setShowRoomModal(true);
          }
        }}
      >
        <div className={style(styles.roomTop)}>
          <div className={style(styles.roomTile(accent))}>
            <KindIcon size={20} />
          </div>
          <div className={style(styles.roomHeading)}>
            <p className={style(styles.roomName)} title={room.name}>
              {room.name}
            </p>
            <span className={style(styles.roomKind)} title={subtitle}>
              {subtitle}
            </span>
          </div>
          {!canEditCapacity && room.isReservableThroughFloorPlan && (
            <span
              className={style(styles.lockIcon)}
              data-tooltip-id={AppConstants.TOOLTIP_GENERAL_ID}
              data-tooltip-content={t('room-capacity-follows-floorplan').toString()}
            >
              <LockOutlineIcon size={16} />
            </span>
          )}
        </div>
        <div className={style(styles.seatsRow)}>
          <b className={style(styles.seatsValue)}>{room.capacity}</b>
          <span className={style(styles.seatsLabel)}>{room.capacity === 1 ? t('seat') : t('seats')}</span>
        </div>
        <div className={style(styles.capacityBar)}>
          <i className={style(styles.capacityBarFill(accent, barPercentage))} />
        </div>
      </div>
      <EditRoomModal
        showModal={showRoomModal}
        initialRoom={room}
        isRoomEmptyOrInUse={isEmptyOrInUse}
        handleSave={handleChange}
        handleClose={(): void => {
          setShowRoomModal(false);
        }}
        onDelete={onDelete}
        canEditCapacity={canEditCapacity}
        floorplanEnabled={floorplanEnabled}
      />
    </>
  );
};

export default RoomCard;
